import React from 'react';
import { 
  Paper, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Box,
  Rating,
  Chip,
  Divider,
  Button
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';

const RankingsList = ({ businesses, searchedBusiness, searchKeyword, onBusinessClick, selectedBusiness }) => {
  // All businesses are displayed
  const displayBusinesses = businesses;
  
  // Find the target business and its position
  const targetBusiness = businesses.find(business => business.isTarget);
  const targetPosition = targetBusiness ? businesses.indexOf(targetBusiness) + 1 : null;

  return (
    <Paper 
      elevation={2}
      sx={{ 
        p: 0,
        borderRadius: 2,
        background: '#fff',
        maxHeight: 'calc(100vh - 150px)', // Increased height to show more items
        overflow: 'auto'
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ color: '#1a73e8' }}>
          Search Results
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Keyword:
          </Typography>
          <Chip
            label={searchKeyword}
            size="small"
            sx={{ ml: 1, bgcolor: '#e8f0fe', color: '#1967d2' }}
          />
        </Box>
        
        {targetBusiness && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: '#f8f9ff', 
            borderRadius: 1, 
            border: '1px solid #e0e0e0',
            borderLeft: `4px solid ${targetPosition <= 3 ? '#4caf50' : 
                                    targetPosition <= 10 ? '#ff9800' : 
                                    '#f44336'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon sx={{ 
                color: targetPosition <= 3 ? '#4caf50' : 
                       targetPosition <= 10 ? '#ff9800' : 
                       '#f44336', 
                mr: 1 
              }} />
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 500, 
                color: targetPosition <= 3 ? '#4caf50' : 
                       targetPosition <= 10 ? '#ff9800' : 
                       '#f44336'
              }}>
                Your Business Position
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: targetPosition <= 3 ? '#4caf50' : 
                         targetPosition <= 10 ? '#ff9800' : 
                         '#f44336'
                }}>
                  #{targetPosition}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {targetBusiness.name}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'inline-block',
                  mt: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: targetPosition <= 3 ? 'rgba(76, 175, 80, 0.1)' : 
                          targetPosition <= 10 ? 'rgba(255, 152, 0, 0.1)' : 
                          'rgba(244, 67, 54, 0.1)',
                  color: targetPosition <= 3 ? '#4caf50' : 
                         targetPosition <= 10 ? '#ff9800' : 
                         '#f44336'
                }}>
                  {targetPosition <= 3 ? 'Excellent' : 
                   targetPosition <= 10 ? 'Good' : 
                   'Needs Improvement'}
                </Typography>
              </Box>
              <Button 
                variant="contained" 
                color={targetPosition <= 3 ? 'success' : 
                       targetPosition <= 10 ? 'warning' : 
                       'error'}
                size="small"
                startIcon={<LocationOnIcon />}
                onClick={() => onBusinessClick(targetBusiness)}
                sx={{ borderRadius: 2 }}
              >
                View on Map
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <List sx={{ width: '100%', py: 0 }}>
        {displayBusinesses.map((business, index) => (
          <ListItem
            key={business.place_id}
            onClick={() => onBusinessClick(business)}
            sx={{
              py: 2,
              px: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: business.isTarget ? '#f8f9ff' : 'transparent',
              cursor: 'pointer',
              position: 'relative',
              '&:hover': {
                bgcolor: '#f5f5f5'
              },
              '&::before': business.isTarget ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                backgroundColor: '#4568dc',
                borderRadius: '4px 0 0 4px'
              } : {}
            }}
          >
            <ListItemIcon>
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: business.isTarget ? '50%' : '4px',
                  bgcolor: business.isTarget ? '#4568dc' : 
                           (index < 3 ? '#4caf50' : // Green for top 3
                            index >= 3 && index < 10 ? '#ff9800' : // Warning color for 4-10
                            '#f44336'), // Red for the rest
                  color: '#fff', // White text for all for better contrast
                  fontWeight: 'bold',
                  fontSize: '14px',
                  mr: 1,
                  border: business.isTarget ? '2px solid #1a73e8' : 'none',
                  boxShadow: business.isTarget ? '0 0 0 2px rgba(26, 115, 232, 0.2)' : 'none'
                }}
              >
                {index + 1}
              </Box>
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {/* Business Name */}
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: business.isTarget ? '#1a73e8' : '#202124',
                      fontWeight: business.isTarget ? 600 : 500
                    }}
                  >
                    {business.name}
                    {business.isTarget && (
                      <Chip 
                        size="small" 
                        label="Your Business" 
                        sx={{ 
                          ml: 1, 
                          height: 20, 
                          fontSize: '0.7rem',
                          bgcolor: '#e8f0fe', 
                          color: '#1967d2',
                          fontWeight: 500
                        }} 
                      />
                    )}
                  </Typography>
                  
                  {/* Rating and Reviews */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Rating
                      value={business.rating || 0}
                      readOnly
                      size="small"
                      precision={0.1}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {business.rating ? business.rating.toFixed(1) : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({business.user_ratings_total || 0} reviews)
                    </Typography>
                  </Box>
                  
                  {/* Address */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <PlaceIcon sx={{ fontSize: 16, mr: 0.5, color: '#666' }} />
                      {business.formatted_address || business.vicinity || business.name}
                    </Typography>
                  </Box>
                  
                  {/* Visibility Score and Business Type */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {business.types && business.types.length > 0 && (
                        <Chip 
                          size="small" 
                          label={business.types[0].replace(/_/g, ' ')} 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            bgcolor: '#f5f5f5',
                            mr: 0.5
                          }} 
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ mr: 0.5, color: '#666' }}>
                        Visibility:
                      </Typography>
                      <Box sx={{
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: index < 3 ? 'rgba(76, 175, 80, 0.1)' : 
                                 index < 10 ? 'rgba(255, 152, 0, 0.1)' : 
                                 'rgba(244, 67, 54, 0.1)',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1
                      }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: index < 3 ? '#4caf50' : 
                                   index < 10 ? '#ff9800' : 
                                   '#f44336'
                          }}
                        >
                          {index < 3 ? 'High' : index < 10 ? 'Medium' : 'Low'} 
                          ({Math.max(0, 100 - (index * 7)).toFixed(0)}%)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default RankingsList;
